import { DEFAULT_FILTER_VALUES, FILTER_TYPES } from '../constants/filterTypes';

/**
 * Utility class for managing ShopifyOrders filter operations.
 * Mirrors the behavior used in Orders module, but scoped to Shopify filters.
 */
export class FilterManager {
  static getDefaultFilters() {
    return {
      searchText: DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH],
      selectedDateRange: null, // Clear to null instead of default 45 days range
      selectedStore: DEFAULT_FILTER_VALUES[FILTER_TYPES.STORE],
      selectedFinancialStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.FINANCIAL_STATUS],
      selectedFulfillmentStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_STATUS],
      selectedShipmentStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_STATUS],
      selectedShipmentDetail: DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_DETAIL],
      selectedConfirmShipment: DEFAULT_FILTER_VALUES[FILTER_TYPES.CONFIRM_SHIPMENT],
      selectedCategory: DEFAULT_FILTER_VALUES[FILTER_TYPES.CATEGORY],
      selectedCollection: DEFAULT_FILTER_VALUES[FILTER_TYPES.COLLECTION],
      selectedPrinter: DEFAULT_FILTER_VALUES[FILTER_TYPES.PRINTER],
      selectedSupporter: DEFAULT_FILTER_VALUES[FILTER_TYPES.SUPPORTER],
      selectedTitle: DEFAULT_FILTER_VALUES[FILTER_TYPES.TITLE],
      selectedSku: DEFAULT_FILTER_VALUES[FILTER_TYPES.SKU],
      selectedEmployeeId: DEFAULT_FILTER_VALUES[FILTER_TYPES.EMPLOYEE_ID],
      selectedExcludeTitle: DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_TITLE],
      selectedExcludeSku: DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_SKU],
      selectedBasecost: DEFAULT_FILTER_VALUES[FILTER_TYPES.BASECOST]
    };
  }

  static countActiveFilters(filters = {}, { lockedEmployeeId = null } = {}) {
    const {
      searchText = DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH],
      selectedDateRange = DEFAULT_FILTER_VALUES[FILTER_TYPES.DATE_RANGE],
      selectedStore = DEFAULT_FILTER_VALUES[FILTER_TYPES.STORE],
      selectedFinancialStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.FINANCIAL_STATUS],
      selectedFulfillmentStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_STATUS],
      selectedShipmentStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_STATUS],
      selectedShipmentDetail = DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_DETAIL],
      selectedConfirmShipment = DEFAULT_FILTER_VALUES[FILTER_TYPES.CONFIRM_SHIPMENT],
      selectedCategory = DEFAULT_FILTER_VALUES[FILTER_TYPES.CATEGORY],
      selectedCollection = DEFAULT_FILTER_VALUES[FILTER_TYPES.COLLECTION],
      selectedPrinter = DEFAULT_FILTER_VALUES[FILTER_TYPES.PRINTER],
      selectedSupporter = DEFAULT_FILTER_VALUES[FILTER_TYPES.SUPPORTER],
      selectedTitle = DEFAULT_FILTER_VALUES[FILTER_TYPES.TITLE],
      selectedSku = DEFAULT_FILTER_VALUES[FILTER_TYPES.SKU],
      selectedEmployeeId = DEFAULT_FILTER_VALUES[FILTER_TYPES.EMPLOYEE_ID],
      selectedExcludeTitle = DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_TITLE],
      selectedExcludeSku = DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_SKU],
      selectedBasecost = DEFAULT_FILTER_VALUES[FILTER_TYPES.BASECOST]
    } = filters;

    const isArrayActive = (v) => Array.isArray(v) ? v.length > 0 : (v && v !== 'all');

    let count = 0;
    if (searchText && String(searchText).trim()) count++;
    if (selectedDateRange && Array.isArray(selectedDateRange) && selectedDateRange.length === 2) count++;
    if (isArrayActive(selectedStore)) count++;
    if (isArrayActive(selectedFinancialStatus)) count++;
    if (isArrayActive(selectedFulfillmentStatus)) count++;
    if (isArrayActive(selectedShipmentStatus)) count++;
    if (selectedShipmentDetail && selectedShipmentDetail !== 'all') count++;
    if (selectedConfirmShipment && selectedConfirmShipment !== 'all') count++;
    if (isArrayActive(selectedCategory)) count++;
    if (isArrayActive(selectedCollection)) count++;
    if (isArrayActive(selectedPrinter)) count++;
    if (isArrayActive(selectedSupporter)) count++;
    if (selectedTitle && String(selectedTitle).trim()) count++;
    if (selectedSku && String(selectedSku).trim()) count++;
    // Không đếm employeeId nếu nó là giá trị bị lock (tự động áp dụng)
    if (selectedEmployeeId && String(selectedEmployeeId).trim() && selectedEmployeeId !== lockedEmployeeId) count++;
    if (selectedExcludeTitle && String(selectedExcludeTitle).trim()) count++;
    if (selectedExcludeSku && String(selectedExcludeSku).trim()) count++;
    if (selectedBasecost && selectedBasecost !== 'all') count++;

    return count;
  }

  static hasUnappliedChanges(localFilters = {}, appliedFilters = {}) {
    const defaults = FilterManager.getDefaultFilters();

    const applied = {
      ...defaults,
      ...appliedFilters
    };

    const normalizeDateRange = (value) => {
      if (!value) return null;
      if (!Array.isArray(value) || value.length !== 2) return null;
      // dayjs objects stringify to ISO-ish; we only need stable comparison
      return [String(value[0]), String(value[1])];
    };

    const normalizeArray = (v) => JSON.stringify(Array.isArray(v) ? v : (v && v !== 'all' ? [v] : []));

    return (
      (localFilters.searchText || '') !== (applied.searchText || '') ||
      JSON.stringify(normalizeDateRange(localFilters.selectedDateRange)) !==
        JSON.stringify(normalizeDateRange(applied.selectedDateRange)) ||
      normalizeArray(localFilters.selectedStore) !== normalizeArray(applied.selectedStore) ||
      normalizeArray(localFilters.selectedFinancialStatus) !== normalizeArray(applied.selectedFinancialStatus) ||
      normalizeArray(localFilters.selectedFulfillmentStatus) !== normalizeArray(applied.selectedFulfillmentStatus) ||
      normalizeArray(localFilters.selectedShipmentStatus) !== normalizeArray(applied.selectedShipmentStatus) ||
      (localFilters.selectedShipmentDetail || 'all') !== (applied.selectedShipmentDetail || 'all') ||
      (localFilters.selectedConfirmShipment || 'all') !== (applied.selectedConfirmShipment || 'all') ||
      normalizeArray(localFilters.selectedCategory) !== normalizeArray(applied.selectedCategory) ||
      normalizeArray(localFilters.selectedCollection) !== normalizeArray(applied.selectedCollection) ||
      normalizeArray(localFilters.selectedPrinter) !== normalizeArray(applied.selectedPrinter) ||
      normalizeArray(localFilters.selectedSupporter) !== normalizeArray(applied.selectedSupporter) ||
      (localFilters.selectedBasecost || 'all') !== (applied.selectedBasecost || 'all')
    );
  }
}
